import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { Translation, useIsMounted } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty, partition } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import CrmZone from '@swf/components/CrmZone/CrmZone';
import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import PlusIcon from '@swf/core/assets/icons/plus-icon.svg';
import { CRMZone } from '@swf/core/enums';
import { useNativeLoader } from '@swf/core/hooks/useNativeLoader';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { isHardVerificationLicense } from '@swf/core/models/auth/utils';
import { select } from '@swf/core/store';

import type { HelpSection } from '../../../../types/Cashier';
import { executeHistoryPush } from '../../../../utils/cashier';
import Alert from '../../../Alert/Alert';
import BonusSummary from '../../../BonusSummary/BonusSummary';
import Container from '../../../Container/Container';
import ListItem from '../../../ListItem/ListItem';
import PaymentMethodSelect from '../../../PaymentMethodSelect/PaymentMethodSelect';
import SuggestedPayment from '../../../Prompts/SuggestedPayment/SuggestedPayment';
import { duration, ltr, rtl } from '../../../WizardGroup/components/Transition/Transition';
import useDepositGuardsData from '../../useDepositGuardsData';
import HelpSectionContent from '../DepositMethod/components/HelpSectionContent/HelpSectionContent';
import DepositTransitionScreen from '../DepositTransitionScreen/DepositTransitionScreen';

import {
  TXTCLI_Other_payment_methods,
  TXTWCL_Add_new_payment_method,
  TXTWCL_Bonus_ready_payment_methods,
  TXTWCL_Deposit,
  TXTWCL_Help_with_deposits,
  TXTWCL_How_would_you_like_to_depositX,
  TXTWCL_How_would_you_like_to_depositX_ELL,
  TXTWCL_Payment_methods,
  TXTWCL_Preferred_payment_method,
  TXTWCL_Sorry_your_saved_payment_metho_ELL,
  TXTWCL_Your_other_payment_method,
  TXTWCL_Your_other_payment_methods,
  TXTWCL_Your_payment_method,
  TXTWCL_Your_payment_methods
} from './DepositMethod.messages.json';
import {
  alert,
  alertHeading,
  container,
  crm,
  gridLayout,
  innerPage,
  listItem,
  plusIcon,
  transitionContainer,
  transitionInnerContainer,
  wrapper
} from './DepositMethod.scss';
import BonusIneligiblePrompt from './components/BonusIneligiblePrompt/BonusIneligiblePrompt';
import PaymentMethodsGroup from './components/PaymentMethodsGroup/PaymentMethodsGroup';
import TempAccountMessage from './components/TempAccountMessage/TempAccountMessage';

const pageDisplayName = 'DepositMethod';

const DepositMethod: React.FC = () => {
  const {
    values: { firstName: wizardFirstName, deposit: wizardDepositValues },
    data
  } = useWizardContext();

  const {
    savedCards = [],
    bonusReadySystems,
    crmBonusCode,
    paySystems,
    rmfPassed,
    signupPassed,
    cmsHeaderAndGridLayout,
    pendingCashouts,
    mitIdResultContext
  } = data;

  const { bonusCode } = wizardDepositValues || {};

  const isReloader = useSelector(select.account.isReloader);
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const license = useSelector(select.account.getLicense);
  const isLimitedAccount = useSelector(select.account.isLimitedAccount);
  const hardVerification = isHardVerificationLicense(license);
  const isHardVerificationTempAccount = hardVerification && isLimitedAccount;
  const firstName = useSelector(select.account.getFirstName) || wizardFirstName;
  const isSWCWelcomeBonusScreenEnabled = useFeature(FeatureFlag.SwcWelcomeBonusScreen);
  const isSWCAffiliateBonusEnabled = useFeature(FeatureFlag.SwcAffiliateBonus);
  const isManageDepositLimitsEnabled = useFeature(FeatureFlag.SwcManageDepositLimits);
  const guards = useDepositGuardsData();

  const exitWizard = useWizardExit();
  const { jumpBack } = useWizardJump();
  const allSavedCardsHostile = !!(
    savedCards.length && savedCards.every(({ savedCard }) => savedCard.hostileBin)
  );
  const preferredCardType = allSavedCardsHostile
    ? savedCards.find((card) => card.savedCard.fastDeposit === true)?.savedCard.paySystem
    : undefined;
  const [showSavedCards, setShowSavedCards] = useState(
    !!savedCards.length && !data.regularMethodSelected && !allSavedCardsHostile
  );
  const [skipSavedMethods, setSkipSavedMethods] = useState(
    !!savedCards.length && !!data.regularMethodSelected
  );
  const [showIneligiblePrompt, setShowIneligiblePrompt] = useState(false);
  const [showSuggestedPaymentPrompt, setShowSuggestedPaymentPrompt] =
    useState(allSavedCardsHostile);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const { goPrevious } = useContext(NavigationContext);
  const [bonusIneligibleCallback, setBonusIneligibleCallback] = useState<() => void>();
  const isMounted = useIsMounted();
  const savedMethodsNodeRef = React.useRef(null);
  const regularMethodsNodeRef = React.useRef(null);

  useNativeLoader();

  const showTempAccountMessage =
    isHardVerificationTempAccount && (!savedCards.length || allSavedCardsHostile);
  const applyGridLayout = cmsHeaderAndGridLayout && !isMobileEmbedded;

  const showWelcomeBonus = guards.showWelcomeBonusOffer({ data });

  const navigateBack =
    (showWelcomeBonus && !signupPassed && !rmfPassed) ||
    (signupPassed && showWelcomeBonus) ||
    (showWelcomeBonus && rmfPassed && !!mitIdResultContext) ||
    !isEmpty(pendingCashouts) ||
    (showWelcomeBonus && rmfPassed);

  const isFirstScreen =
    isEmpty(pendingCashouts) &&
    (!isSWCWelcomeBonusScreenEnabled ||
      !(isSWCAffiliateBonusEnabled || crmBonusCode) ||
      isReloader ||
      isMobileEmbedded ||
      hideBonusCode);

  const showExitPrompt = () => {
    if (isMobileEmbedded) {
      return savedCards.length ? !showSavedCards : false;
    }
    return !(rmfPassed && isEmpty(wizardDepositValues));
  };
  const [otherSystems, bonusSystems] = partition(paySystems, (system) =>
    bonusReadySystems ? !bonusReadySystems.includes(system.cardType) : paySystems
  );
  const [otherSavedSystems, fastDepositSystem] = partition(
    savedCards,
    ({ savedCard }) => !savedCard.fastDeposit
  );
  const subtitle = (
    <Translation
      {...(firstName
        ? TXTWCL_How_would_you_like_to_depositX_ELL
        : TXTWCL_How_would_you_like_to_depositX)}
      values={{ 0: firstName }}
      tagName="h2"
    />
  );

  const handleNavigateBack = useCallback(() => {
    if (!isEmpty(pendingCashouts)) {
      jumpBack('#deposit.deposit-pending-cashout-menu');
    } else if (showWelcomeBonus) {
      jumpBack('#deposit.bonus-offer');
    } else {
      goPrevious();
    }
  }, [pendingCashouts, showWelcomeBonus, jumpBack, goPrevious]);

  const handleSkipSavedMethods = useCallback(() => {
    setIsTransitioning(true);
    setShowSavedCards(true);
    setSkipSavedMethods(false);
    delete data.regularMethodSelected;
  }, [setIsTransitioning, setShowSavedCards, setSkipSavedMethods, data.regularMethodSelected]);

  const goBack = useCallback(() => {
    if (skipSavedMethods) {
      handleSkipSavedMethods();
    } else if (navigateBack) {
      handleNavigateBack();
    } else {
      exitWizard();
    }
  }, [skipSavedMethods, navigateBack, handleNavigateBack, exitWizard, handleSkipSavedMethods]);

  const setTitle = () => {
    if (fastDepositSystem.length) {
      if (otherSavedSystems.length > 1) {
        return TXTWCL_Your_other_payment_methods;
      }
      return TXTWCL_Your_other_payment_method;
    }
    if (otherSavedSystems.length > 1) {
      return TXTWCL_Your_payment_methods;
    }
    return TXTWCL_Your_payment_method;
  };

  const CRMZoneContent = (
    <div className={crm}>
      <CrmZone zoneId={CRMZone.SWC_DEPOSIT_MAIN} componentType="Single" dynamicHeight visible />
    </div>
  );
  const regularMethodsScreen = (
    <div className={wrapper}>
      <BonusSummary page={pageDisplayName} />
      {showTempAccountMessage && <TempAccountMessage />}
      {subtitle}
      {CRMZoneContent}
      {allSavedCardsHostile && (
        <>
          {showSuggestedPaymentPrompt && preferredCardType && (
            <SuggestedPayment
              onClose={() => setShowSuggestedPaymentPrompt(false)}
              cardType={preferredCardType}
              paySystems={paySystems}
            />
          )}
          <Alert variant="warning" className={alert}>
            <>
              <div className={alertHeading}>
                <Translation {...TXTWCL_Payment_methods} />
              </div>
              <div>
                <Translation {...TXTWCL_Sorry_your_saved_payment_metho_ELL} />
              </div>
            </>
          </Alert>
        </>
      )}
      {!!bonusSystems.length && (
        <PaymentMethodsGroup
          options={bonusSystems}
          label={TXTWCL_Bonus_ready_payment_methods}
          variant="bonusReady"
          disable
          embeddedPropValue
        />
      )}
      {!!otherSystems.length && (
        <PaymentMethodsGroup
          options={otherSystems}
          label={bonusSystems.length ? TXTCLI_Other_payment_methods : TXTWCL_Payment_methods}
          variant="otherPaymentMethods"
          disable={!bonusSystems.length}
          embeddedPropValue={!!bonusCode && !bonusSystems.length}
          {...(bonusSystems.length && {
            onClick: (callback: () => void) => {
              setShowIneligiblePrompt(true);
              setBonusIneligibleCallback(() => callback);
            }
          })}
        />
      )}
    </div>
  );

  const savedMethodsScreen = (
    <div className={wrapper}>
      <BonusSummary page={pageDisplayName} />
      {isHardVerificationTempAccount && <TempAccountMessage />}
      {subtitle}
      {CRMZoneContent}
      {!!fastDepositSystem.length && (
        <PaymentMethodSelect
          fieldName="deposit"
          heading={TXTWCL_Preferred_payment_method}
          options={fastDepositSystem}
          disable
          isSavedCard
        />
      )}
      {!!otherSavedSystems.length && (
        <PaymentMethodSelect
          fieldName="deposit"
          heading={setTitle()}
          options={otherSavedSystems}
          disable
          isSavedCard
        />
      )}
      <ListItem
        primary={<Translation {...TXTWCL_Add_new_payment_method} />}
        avatar={<PlusIcon />}
        classes={{
          avatar: classNames(plusIcon, { [gridLayout]: applyGridLayout }),
          root: classNames(listItem, { [gridLayout]: applyGridLayout })
        }}
        onClick={() => {
          delete data.savedCardInfoId;
          setSkipSavedMethods(true);
          if (canUseDOM && applyGridLayout) {
            (document.documentElement as HTMLElement).scrollTop = 0;
          }
        }}
      />
    </div>
  );

  useEffect(() => {
    if (skipSavedMethods && isMounted()) {
      setShowSavedCards(false);
      setIsTransitioning(true);
      isMobileEmbedded && executeHistoryPush();
    }
  }, [isMobileEmbedded, skipSavedMethods, isMounted]);

  const helpSection: HelpSection = {
    title: TXTWCL_Help_with_deposits,
    body: <HelpSectionContent />
  };

  return (
    <Container
      page={DepositMethod}
      layoutClassName={classNames(container, {
        [gridLayout]: applyGridLayout
      })}
      pageClassName={classNames(innerPage, { [gridLayout]: applyGridLayout })}
      headerProps={{
        title: <Translation {...TXTWCL_Deposit} />,
        buttons: {
          back: !rmfPassed || navigateBack,
          onBackClick: goBack,
          allowMultiClick: skipSavedMethods
        },
        showExitPrompt: showExitPrompt()
      }}
      hideBonusSummary
      helpSection={helpSection}
      limitsSection={isManageDepositLimitsEnabled}
      helpSupportResolver
      showFooter={!cmsHeaderAndGridLayout}
    >
      {isFirstScreen && <DepositTransitionScreen />}

      {showIneligiblePrompt && (
        <BonusIneligiblePrompt
          onClose={() => setShowIneligiblePrompt(false)}
          bonusIneligibleCallback={bonusIneligibleCallback}
        />
      )}

      {isMobileEmbedded ? (
        <CSSTransition
          in={showSavedCards}
          timeout={duration}
          onExited={() => {
            setIsTransitioning(false);
          }}
          classNames={!showSavedCards ? rtl : ltr}
          unmountOnExit
          nodeRef={savedMethodsNodeRef}
        >
          <div
            ref={savedMethodsNodeRef}
            className={classNames({ [transitionContainer]: isTransitioning })}
          >
            <div className={classNames({ [transitionInnerContainer]: isTransitioning })}>
              {savedMethodsScreen}
            </div>
          </div>
        </CSSTransition>
      ) : (
        showSavedCards && <div>{savedMethodsScreen}</div>
      )}
      {isMobileEmbedded ? (
        <CSSTransition
          in={!showSavedCards}
          timeout={duration}
          classNames={!showSavedCards ? rtl : ltr}
          unmountOnExit
          onEntered={() => {
            setIsTransitioning(false);
          }}
          nodeRef={regularMethodsNodeRef}
        >
          <div
            ref={regularMethodsNodeRef}
            className={classNames({ [transitionContainer]: isTransitioning })}
          >
            <div className={classNames({ [transitionInnerContainer]: isTransitioning })}>
              {regularMethodsScreen}
            </div>
          </div>
        </CSSTransition>
      ) : (
        !showSavedCards && <div>{regularMethodsScreen}</div>
      )}
    </Container>
  );
};

DepositMethod.displayName = pageDisplayName;
export default DepositMethod;
